// src/components/FanForm.jsx
import React from 'react';
import PropTypes from 'prop-types';
import ProductGroupSelector from './ProductGroupSelector';

/**
 * FanForm
 *
 * Props:
 *  - formData: object with fields (airFlow, airFlowTolNeg, airFlowTolPos, staticPressure, staticPressureTolNeg, staticPressureTolPos, frequency, categorySelect, ...)
 *  - setFormData: setter for formData (from parent useState)
 *  - onSubmit: form submit handler (parent should accept the event)
 *  - categories: array of product groups (passed to ProductGroupSelector)
 *  - selectedItems: currently selected product group sub-items
 *  - setSelectedItems: setter for selectedItems
 *
 * This component is intentionally "dumb" — keeps no business logic, only UI and small input normalization.
 */

// Helper: build final image src using REACT_APP_API_BASE_URL if present
const ensureProtocol = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || /^\/\//.test(url)) return url;
  // add http:// if user accidentally omitted protocol (useful for local dev envs)
  return `http://${url}`;
};

const buildImgSrc = (candidate) => {
  const defaultPath = '/images/41.png';
  const rawBase = process.env.REACT_APP_API_BASE_URL || '';
  const base = rawBase ? rawBase.replace(/\/+$/, '') : '';

  let c = candidate || defaultPath;
  let result = '';

  // if candidate already absolute (http(s) or protocol-relative) -> return as-is
  if (/^(https?:)?\/\//i.test(c)) {
    result = c;
  }
  // leading slash => root-relative, join with base if available
  else if (c.startsWith('/')) {
    if (base) {
      const withProto = ensureProtocol(base);
      try {
        result = new URL(c, withProto).toString();
      } catch (e) {
        result = `${withProto.replace(/\/$/, '')}${c}`;
      }
    } else {
      result = c; // e.g. public asset
    }
  }
  // relative path (no leading slash) -> join with base if available
  else if (base) {
    const withProto = ensureProtocol(base);
    try {
      result = new URL(`/${c}`, withProto).toString();
    } catch (e) {
      result = `${withProto.replace(/\/$/, '')}/${c}`;
    }
  } else {
    result = c;
  }

  // Replace api.apt.com.co with apt.com.co for images
  return result.replace(/api\.apt\.com\.co/gi, 'apt.com.co');
};

export default function FanForm({
  formData,
  setFormData,
  onSubmit,
  categories = [],
  selectedItems = [],
  setSelectedItems,
  loading,
}) {
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const nextValue = type === 'number' && value === '' ? '' : value;
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  // Called by ProductGroupSelector when user picks subgroups
  const handleGroupSelect = (arr) => {
    const items = arr || [];
    setSelectedItems(items);
    setFormData((prev) => ({
      ...prev,
      categorySelect: items.map((s) => s.name).join(', '),
    }));
  };

  const handleRemoveSelected = (id) => {
    const next = (selectedItems || []).filter((s) => String(s.id) !== String(id));
    setSelectedItems(next);
    setFormData((prev) => ({
      ...prev,
      categorySelect: next.map((s) => s.name).join(', '),
    }));
  };

  const validate = () => {
    // Check if at least one category is selected
    if (!selectedItems || selectedItems.length === 0) {
      return { ok: false, msg: 'Please select at least one product category before searching.' };
    }

    const af = Number(formData.airFlow);
    const sp = Number(formData.staticPressure);
    if (!Number.isFinite(af) || af <= 0) return { ok: false, msg: 'Please enter a valid Air Flow (> 0).' };
    if (!Number.isFinite(sp) || sp < 0) return { ok: false, msg: 'Please enter a valid Static Pressure (>= 0).' };

    const tolFields = ['airFlowTolNeg', 'airFlowTolPos', 'staticPressureTolNeg', 'staticPressureTolPos'];
    for (const f of tolFields) {
      if (formData[f] !== '' && formData[f] !== undefined && Number.isNaN(Number(formData[f]))) {
        return { ok: false, msg: 'Tolerances must be numeric values (or left empty).' };
      }
    }

    return { ok: true };
  };

  const internalSubmit = (e) => {
    const v = validate();
    if (!v.ok) {
      e.preventDefault();
      e.stopPropagation();
      // eslint-disable-next-line no-alert
      alert(v.msg);
      return;
    }
    onSubmit?.(e);
  };

  return (
    <form onSubmit={internalSubmit} noValidate>
      {/* Product Group */}
      <div className="form-group">
        <label className="font-weight-medium">Product Group</label>
        <ProductGroupSelector productGroups={categories} onSelect={handleGroupSelect} selectedItems={selectedItems} />
        {formData.categorySelect && (
          <small className="form-text text-success">Selected: {formData.categorySelect}</small>
        )}

        {selectedItems && selectedItems.length > 0 && (
          <div className="mt-2 d-flex flex-wrap">
            {selectedItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleRemoveSelected(item.id)}
                className="d-flex align-items-center mr-2 p-1"
                title="Click to remove"
                style={{ minWidth: 120, cursor: 'pointer', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8 }}
              >
                <img
                  src={buildImgSrc(item.img || item.image || item.image_url || item.icon || '/images/41.png')}
                  alt={item.name}
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: 'contain',
                    borderRadius: 6,
                    marginRight: 8,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                  }}
                />
                <div className="small text-truncate" style={{ maxWidth: 120 }}>{item.name}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Air Flow */}
      <div className="form-group">
        <label className="font-weight-medium">Air Flow</label>
        <div className="input-group flex-nowrap">
          <input
            type="number"
            className="form-control"
            name="airFlow"
            value={formData.airFlow ?? ''}
            onChange={handleChange}
            step="any"
            min="0"
            required
            aria-label="Air Flow"
            placeholder="e.g. 1200"
          />
          <select
            className="form-control"
            name="airFlowUnit"
            value={formData.airFlowUnit ?? 'm3h'}
            onChange={handleChange}
            style={{ 
              flex: '0 0 auto',
              minWidth: '100px',
              maxWidth: '120px',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
          >
            <option value="m3h">m³/h</option>
            <option value="m3s">m³/s</option>
            <option value="ls">l/s</option>
            <option value="cfm">cfm</option>
          </select>
        </div>
      </div>

      {/* Air Flow Tolerances */}
      <div className="form-group">
        <label className="font-weight-medium">Air Flow Tolerance (%)</label>
        <div className="row g-2">
          <div className="col-6">
            <input
              type="number"
              className="form-control"
              name="airFlowTolNeg"
              value={formData.airFlowTolNeg ?? ''}
              onChange={handleChange}
              placeholder="- (e.g. 5)"
              step="5"
            />
          </div>
          <div className="col-6">
            <input
              type="number"
              className="form-control"
              name="airFlowTolPos"
              value={formData.airFlowTolPos ?? ''}
              onChange={handleChange}
              placeholder="+ (e.g. 10)"
              step="5"
            />
          </div>
        </div>
      </div>

      {/* Static Pressure */}
      <div className="form-group">
        <label className="font-weight-medium">Static Pressure</label>
        <div className="input-group flex-nowrap">
          <input
            type="number"
            className="form-control"
            name="staticPressure"
            value={formData.staticPressure ?? ''}
            onChange={handleChange}
            step="any"
            min="0"
            required
            aria-label="Static Pressure"
            placeholder="e.g. 200"
          />
          <select
            className="form-control"
            name="staticPressureUnit"
            value={formData.staticPressureUnit ?? 'pa'}
            onChange={handleChange}
            style={{ 
              flex: '0 0 auto',
              minWidth: '100px',
              maxWidth: '120px',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
          >
            <option value="pa">Pa</option>
            <option value="mmh2o">mm H₂O</option>
            <option value="inchh2o">inch H₂O</option>
            <option value="mbar">mbar</option>
            <option value="kpa">kPa</option>
          </select>
        </div>
      </div>

      {/* Static Pressure Tolerances */}
      <div className="form-group">
        <label className="font-weight-medium">Static Pressure Tolerance (%)</label>
        <div className="row g-2">
          <div className="col-6">
            <input
              type="number"
              className="form-control"
              name="staticPressureTolNeg"
              value={formData.staticPressureTolNeg ?? ''}
              onChange={handleChange}
              placeholder="-"
              step="5"
            />
          </div>
          <div className="col-6">
            <input
              type="number"
              className="form-control"
              name="staticPressureTolPos"
              value={formData.staticPressureTolPos ?? ''}
              onChange={handleChange}
              placeholder="+"
              step="5"
            />
          </div>
        </div>
      </div>

      {/* Frequency */}
      <div className="form-group">
        <label className="font-weight-medium d-block">Frequency</label>
        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="radio"
            name="frequency"
            id="freq50"
            value="50"
            checked={String(formData.frequency) === '50'}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="freq50">50 Hz</label>
        </div>
        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="radio"
            name="frequency"
            id="freq60"
            value="60"
            checked={String(formData.frequency) === '60'}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="freq60">60 Hz</label>
        </div>
      </div>

      {/* Submit */}
      <div className="form-group">
        <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
          {loading ? 'Searching…' : 'Find Products'}
        </button>
      </div>
    </form>
  );
}

FanForm.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  categories: PropTypes.array,
  selectedItems: PropTypes.array,
  setSelectedItems: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

FanForm.defaultProps = {
  categories: [],
  selectedItems: [],
  loading: false,
};
